import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import { resolveApiErrorMessage } from 'src/app/core/http/api-http-error';
import {
  EntityRow,
  PromptCodeSpec,
  RulesApiService,
} from 'src/app/core/services/rules-api.service';
import type { EntityFieldOption } from 'src/app/shared/components/entity-catalog/entity-catalog.types';
import { formatDeletedAt } from 'src/app/shared/components/entity-catalog/entity-catalog-deleted.util';
import { DeleteConfirmModalComponent } from 'src/app/shared/components/delete-confirm-modal/delete-confirm-modal.component';
import { RestoreConfirmModalComponent } from 'src/app/shared/components/restore-confirm-modal/restore-confirm-modal.component';
import { toSelectableCountryPackOptions } from '../country-packs/country-pack-options';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { IconModule } from 'src/app/shared/icon/icon.module';
import {
  filterRecordsBySearch,
  filterRecordsByStatus,
  paginateClientRecords,
} from 'src/app/shared/utils/client-list.util';
import {
  catalogStatusBadgeClass,
  catalogStatusLabel,
} from 'src/app/shared/utils/catalog-status-badge.util';

type PromptView = 'active' | 'deleted';

function textField(record: EntityRow, key: string, fallback = ''): string {
  const value = record[key];
  return typeof value === 'string' ? value : fallback;
}

@Component({
  selector: 'app-prompt-templates',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    ListModuleTableComponent,
    ListCellDefDirective,
    IconModule,
    DeleteConfirmModalComponent,
    RestoreConfirmModalComponent,
  ],
  templateUrl: './prompt-templates.component.html',
})
export class PromptTemplatesComponent implements OnInit {
  private readonly api = inject(RulesApiService);

  readonly toolbarTabs = [{ key: 'deleted', label: 'Deleted' }];

  readonly activeColumns: ListModuleColumnDef[] = [
    { id: 'code', header: 'Prompt' },
    { id: 'country_pack', header: 'Country' },
    { id: 'locale', header: 'Locale' },
    { id: 'version', header: 'Version' },
    { id: 'status', header: 'Status' },
    { id: 'actions', header: 'Actions', align: 'right' },
  ];

  readonly deletedColumns: ListModuleColumnDef[] = [
    { id: 'code', header: 'Prompt' },
    { id: 'country_pack', header: 'Country' },
    { id: 'locale', header: 'Locale' },
    { id: 'version', header: 'Version' },
    { id: 'deleted_at', header: 'Deleted at' },
    { id: 'actions', header: 'Actions', align: 'right' },
  ];

  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {
      search: { placeholder: 'Search prompt, country, locale…' },
      status: {
        options: [
          { value: '', label: 'All status' },
          { value: 'DRAFT', label: 'Draft' },
          { value: 'PUBLISHED', label: 'Published' },
        ],
      },
      dateRange: false,
    },
    pagination: true,
  };

  view: PromptView = 'active';
  catalog: PromptCodeSpec[] = [];
  countryPackOptions: EntityFieldOption[] = [];
  records: EntityRow[] = [];
  loading = false;
  saving = false;
  error = '';
  notice = '';

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };
  currentPage = 1;
  readonly perPage = 10;

  editing: EntityRow | null = null;
  draft: { id?: number; code: string; country_pack: string; locale: string; body: string } = {
    code: '',
    country_pack: '',
    locale: 'en',
    body: '',
  };

  showDeleteModal = false;
  deleteError = '';
  readonly deleteLoading = signal(false);
  selectedForDelete: EntityRow | null = null;

  showRestoreModal = false;
  readonly restoreLoading = signal(false);
  selectedForRestore: EntityRow | null = null;

  readonly statusBadgeClass = catalogStatusBadgeClass;
  readonly statusLabel = catalogStatusLabel;

  ngOnInit(): void {
    this.api
      .getPromptCatalog()
      .pipe(assertSuccessEnvelope())
      .subscribe({
        next: (res) => (this.catalog = res.data ?? []),
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
    this.loadCountryPacks();
    this.load();
  }

  get columns(): ListModuleColumnDef[] {
    return this.view === 'deleted' ? this.deletedColumns : this.activeColumns;
  }

  get activeToolbarTab(): string {
    return this.view === 'deleted' ? 'deleted' : '';
  }

  get listSurfaceConfigView(): ListModuleSurfaceConfig {
    if (this.view === 'deleted') {
      return {
        toolbar: { search: this.listSurfaceConfig.toolbar?.search, dateRange: false },
        pagination: true,
      };
    }
    return this.listSurfaceConfig;
  }

  onToolbarTabChange(key: string): void {
    this.view = this.view === key ? 'active' : (key as PromptView);
    this.error = '';
    this.cancel();
    this.load();
  }

  private loadCountryPacks(): void {
    this.api
      .listCountryPackRecords()
      .pipe(assertSuccessEnvelope())
      .subscribe({
        next: (res) => {
          this.countryPackOptions = toSelectableCountryPackOptions(res.data ?? []);
        },
        error: () => {
          this.countryPackOptions = [];
        },
      });
  }

  get countryPackSelectOptions(): EntityFieldOption[] {
    const current = this.draft.country_pack?.trim();
    if (current && !this.countryPackOptions.some((o) => o.value === current)) {
      return [{ value: current, label: `${current} (saved)` }, ...this.countryPackOptions];
    }
    return this.countryPackOptions;
  }

  get filteredRecords(): EntityRow[] {
    let rows = [...this.records];
    rows = filterRecordsBySearch(rows, this.listFilters.searchQuery, [
      'code',
      'country_pack',
      'locale',
      'status',
      'deleted_at',
    ]);
    if (this.view === 'active') {
      rows = filterRecordsByStatus(rows, this.listFilters.status, 'status');
    }
    return rows;
  }

  get pagedRecords(): EntityRow[] {
    return paginateClientRecords(this.filteredRecords, this.currentPage, this.perPage).pageRows;
  }

  get paginationMeta(): ListModulePaginationMeta {
    return paginateClientRecords(this.filteredRecords, this.currentPage, this.perPage).meta;
  }

  get emptyMessage(): string {
    if (this.view === 'deleted') {
      return this.records.length
        ? 'No deleted prompts match your filters.'
        : 'No deleted prompts yet.';
    }
    return this.records.length
      ? 'No prompts match your filters.'
      : 'No prompts configured — every country is using the built-in prompts.';
  }

  get showEditor(): boolean {
    return this.view === 'active' && (this.editing !== null || !!this.draft.code);
  }

  load(): void {
    this.loading = true;
    this.error = '';
    const request$ =
      this.view === 'deleted' ? this.api.listDeletedPromptRecords() : this.api.listPromptRecords();

    request$
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (res) => {
          const rows = res.data ?? [];
          this.records =
            this.view === 'deleted'
              ? rows.map((row) => ({ ...row, deleted_at: formatDeletedAt(row['deleted_at']) }))
              : rows;
          this.currentPage = 1;
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.currentPage = 1;
  }

  onListPageChange(page: number): void {
    this.currentPage = page;
  }

  specFor(code: unknown): PromptCodeSpec | undefined {
    return this.catalog.find((c) => c.code === code);
  }

  get allowedVariables(): PromptCodeSpec['variables'] {
    return this.specFor(this.draft.code)?.variables ?? [];
  }

  placeholderFor(name: string): string {
    return `{{${name}}}`;
  }

  isDraft(record: EntityRow): boolean {
    return record['status'] === 'DRAFT';
  }

  newPrompt(): void {
    this.editing = null;
    this.error = '';
    this.notice = '';
    this.draft = { code: this.catalog[0]?.code ?? '', country_pack: '', locale: 'en', body: '' };
  }

  edit(record: EntityRow): void {
    this.editing = record;
    this.error = '';
    this.notice = '';
    this.draft = {
      id: Number(record['id']),
      code: textField(record, 'code'),
      country_pack: textField(record, 'country_pack'),
      locale: textField(record, 'locale', 'en'),
      body: textField(record, 'body'),
    };
  }

  cancel(): void {
    this.editing = null;
    this.draft = { code: '', country_pack: '', locale: 'en', body: '' };
  }

  save(): void {
    const used = new Set(
      [...this.draft.body.matchAll(/\{\{\s*(?:[#/^]\s*)?([A-Za-z_]\w*)\s*\}\}/g)].map((m) => m[1]),
    );
    const variables = this.allowedVariables
      .filter((v) => used.has(v.name))
      .map((v) => ({ name: v.name, required: v.required, description: v.description }));

    const body = {
      code: this.draft.code,
      country_pack: this.draft.country_pack || null,
      locale: this.draft.locale,
      body: this.draft.body,
      variables,
    };

    const isDraftRow = this.editing && this.isDraft(this.editing);
    const request$ = isDraftRow
      ? this.api.updatePromptDraft(Number(this.draft.id), body)
      : this.api.createPromptDraft(body);

    this.saving = true;
    this.error = '';
    request$
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (res) => {
          this.notice = res.message || 'Saved';
          this.editing = res.data;
          this.draft.id = Number(res.data['id']);
          this.load();
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  publish(record: EntityRow): void {
    this.saving = true;
    this.error = '';
    this.notice = '';
    this.api
      .publishPrompt(Number(record['id']))
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (res) => {
          this.notice = res.message || 'Published';
          this.editing = null;
          this.cancel();
          this.load();
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  onDelete(row: EntityRow): void {
    this.deleteError = '';
    this.selectedForDelete = row;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedForDelete = null;
    this.deleteError = '';
  }

  submitDelete(): void {
    const row = this.selectedForDelete;
    if (!row) return;

    const id = Number(row['id']);
    if (!Number.isFinite(id) || id <= 0) return;

    this.deleteLoading.set(true);
    this.deleteError = '';
    this.api
      .deletePromptRecord(id)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => this.deleteLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.closeDeleteModal();
          if (this.editing?.['id'] === row['id']) {
            this.cancel();
          }
          this.notice = 'Prompt deleted.';
          this.load();
        },
        error: (err) => (this.deleteError = resolveApiErrorMessage(err)),
      });
  }

  onRestore(row: EntityRow): void {
    this.selectedForRestore = row;
    this.showRestoreModal = true;
  }

  closeRestoreModal(): void {
    this.showRestoreModal = false;
    this.selectedForRestore = null;
  }

  submitRestore(): void {
    const row = this.selectedForRestore;
    if (!row) return;

    const id = Number(row['id']);
    if (!Number.isFinite(id) || id <= 0) return;

    this.restoreLoading.set(true);
    this.error = '';
    this.api
      .restorePromptRecord(id)
      .pipe(
        assertSuccessEnvelope(),
        finalize(() => this.restoreLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.closeRestoreModal();
          this.view = 'active';
          this.notice = 'Prompt restored.';
          this.load();
        },
        error: (err) => (this.error = resolveApiErrorMessage(err)),
      });
  }

  get deleteEntityName(): string {
    return this.promptLabel(this.selectedForDelete);
  }

  get restoreEntityName(): string {
    return this.promptLabel(this.selectedForRestore);
  }

  private promptLabel(row: EntityRow | null): string {
    if (!row) return '';
    const code = textField(row, 'code');
    const country = textField(row, 'country_pack') || 'All countries';
    const version = row['version'];
    return `${code} · ${country} · v${version}`;
  }
}
